/* eslint-disable react/no-array-index-key */
import cx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';

import { ACTION_STATUS_SUCCESS } from '@/actions';
import { getEditorialReviewsData } from '@/actions/new-car/editorial-reviews';
import { TextLink } from '@/components/text-link';
import { VEHICLE_VALUE_ELECTRIC_CARS } from '@/constants/global';
import { joinSgcmMobileSite } from '@/utils/url/join/public';

import {
  PRICE_INFO_SPAN_CONTENT_REBATE,
  PRICE_INFO_SPAN_CONTENT_SURCHARGE,
// eslint-disable-next-line @stylistic/max-len
} from '../../../../_components/page-content/submodel-list/mobile/submodel-info-table/price-info/constants';
import { convertPriceToText } from '../utils';
import styles from './styles.module.scss';
import type { Component } from './types';

export const OverviewTable: Component = ({
  compareList,
  promotonList,
}) => {
  const [
    editorialReviewList,
    setEditorialReviewList,
  ] = useState<(string | null | undefined)[]>([
    undefined,
    undefined,
    undefined,
    undefined,
  ]);

  useEffect(() => {
    const fetchEditorialReviews = async () => {
      const editorialReviews = await Promise.all(
        compareList.map(async (item) => {
          if (item !== null && item.reviewCarId !== null) {
            const formData = new FormData();

            formData.append('carId', item.reviewCarId.toString());
            const actionResult = await getEditorialReviewsData(formData);

            if (actionResult.status === ACTION_STATUS_SUCCESS) {
              if (actionResult.data.data.length) {
                return actionResult.data.data[0].link;
              }
              return null;
            }
          }
          return undefined;
        }),
      );

      setEditorialReviewList(editorialReviews);
    };

    fetchEditorialReviews();
  }, [compareList]);

  return (
    <>
      <div
        className={`${styles.containerStickyRow} ${styles.textLabel}`}>
        Price
      </div>
      <div className={`${styles.containerRow}`}>
        {compareList.map((item, i) => {
          if (item !== null) {
            const priceValue = item.subModels[0]?.price ? `$${item.subModels[0]?.price.toLocaleString()}` : 'POA';
            const promo = promotonList.find(
              promotion => promotion.models.find(({ id }) => id === item.id),
            );
            return (
              <div
                key={`${item.subModels[0]?.id}-price`}
                className={cx(
                  styles.containerScrollCell,
                  styles.textPrice,
                )}>
                {priceValue}
                {promo && (
                  <Link
                    className={styles.textViewPromo}
                    href={
                      promo.models.find(
                        model => model.id === item.id,
                      )?.link ?? ''
                    }>
                    view promo
                  </Link>
                )}
              </div>
            );
          }
          return (
            <div
              key={`null-${i}-price`}
              className={`${styles.containerScrollCell}`}>
              -
            </div>
          );
        })}
      </div>
      <div
        className={`${styles.containerStickyRow} ${styles.textLabel}`}>
        Depreciation
      </div>
      <div className={`${styles.containerRow}`}>
        {compareList.map((item, i) => {
          if (item !== null) {
            const deprecationValue = convertPriceToText(
              item.subModels[0]?.depreciation,
              '/yr',
            );
            return (
              <div
                key={`${item.subModels[0]?.id}-depreciation`}
                className={`${styles.containerScrollCell}`}>
                {deprecationValue}
              </div>
            );
          }
          return (
            <div
              key={`null-${i}-depreciation`}
              className={`${styles.containerScrollCell}`}>
              -
            </div>
          );
        })}
      </div>
      <div
        className={`${styles.containerStickyRow} ${styles.textLabel}`}>
        Down Payment
      </div>
      <div className={`${styles.containerRow}`}>
        {compareList.map((item, i) => {
          if (item !== null) {
            const loanRatio = item.subModels[0]?.priceDetail?.loanToValueRatio
              ?? 1;
            const downPaymentValue = convertPriceToText(item.subModels[0].price
              ? Math.round(item.subModels[0].price * loanRatio)
              : null);
            return (
              <div
                key={`${item.subModels[0]?.id}-down-payment`}
                className={`${styles.containerScrollCell}`}>
                {loanRatio === 1
                  ? (
                    '-'
                  )
                  : (
                    <>
                      {downPaymentValue}
                      {item.subModels[0].price && (
                        <span className={styles.textSpanContent}>
                          {
                            `(Max ${(1 - loanRatio) * 100}% loan)`
                          }
                        </span>
                      )}
                    </>
                  )}
              </div>
            );
          }
          return (
            <div
              key={`null-${i}-down-payment`}
              className={`${styles.containerScrollCell}`}>
              -
            </div>
          );
        })}
      </div>
      <div
        className={`${styles.containerStickyRow} ${styles.textLabel}`}>
        Instalment
      </div>
      <div className={`${styles.containerRow}`}>
        {compareList.map((item, i) => {
          if (item !== null) {
            const installmentValue = convertPriceToText(
              item.subModels[0].priceDetail?.installment,
              '/mth',
            );
            return (
              <div
                key={`${item.subModels[0]?.id}-instalment`}
                className={`${styles.containerScrollCell}`}>
                {installmentValue}
              </div>
            );
          }
          return (
            <div
              key={`null-${i}-instalment`}
              className={`${styles.containerScrollCell}`}>
              -
            </div>
          );
        })}
      </div>
      <div
        className={`${styles.containerStickyRow} ${styles.textLabel}`}>
        COE Quota Premium
      </div>
      <div className={`${styles.containerRow}`}>
        {compareList.map((item, i) => {
          if (item !== null) {
            const coeQuotaPremiumValue = convertPriceToText(
              item.subModels[0]?.priceDetail?.coeQuotaPremium,
            );
            return (
              <div
                key={`${item.subModels[0]?.id}-coe-quota-premium`}
                className={`${styles.containerScrollCell}`}>
                {coeQuotaPremiumValue}
                {item.subModels[0]?.priceDetail.coeQuotaPremium
                && (
                  <span className={styles.textSpanContent}>
                    (CAT
                    {' '}
                    {item.subModels[0]?.priceDetail?.coeCategory.toUpperCase()}
                    )
                  </span>
                )}
              </div>
            );
          }
          return (
            <div
              key={`null-${i}-coe-quota-premium`}
              className={`${styles.containerScrollCell}`}>
              -
            </div>
          );
        })}
      </div>
      <div
        className={`${styles.containerStickyRow} ${styles.textLabel}`}>
        Road Tax
      </div>
      <div className={`${styles.containerRow}`}>
        {compareList.map((item, i) => {
          if (item !== null) {
            const roadTaxValue = convertPriceToText(
              item.subModels[0]?.priceDetail?.roadTax,
              '/yr',
            );
            return (
              <div
                key={`${item.subModels[0]?.id}-road-tax`}
                className={`${styles.containerScrollCell}`}>
                {roadTaxValue}
              </div>
            );
          }
          return (
            <div
              key={`null-${i}-road-tax`}
              className={`${styles.containerScrollCell}`}>
              -
            </div>
          );
        })}
      </div>
      <div
        className={`${styles.containerStickyRow} ${styles.textLabel}`}>
        OMV
      </div>
      <div className={`${styles.containerRow}`}>
        {compareList.map((item, i) => {
          if (item !== null) {
            const omvValue = convertPriceToText(
              item.subModels[0]?.priceDetail?.omv,
            );
            return (
              <div
                key={`${item.subModels[0]?.id}-omv`}
                className={`${styles.containerScrollCell}`}>
                {omvValue}
              </div>
            );
          }
          return (
            <div
              key={`null-${i}-omv`}
              className={`${styles.containerScrollCell}`}>
              -
            </div>
          );
        })}
      </div>
      <div
        className={`${styles.containerStickyRow} ${styles.textLabel}`}>
        ARF
      </div>
      <div className={`${styles.containerRow}`}>
        {compareList.map((item, i) => {
          if (item !== null) {
            const arfValue = convertPriceToText(
              item.subModels[0]?.priceDetail?.arf,
            );
            const isNegativeVes = item.subModels[0]?.priceDetail?.ves
              && item.subModels[0]?.priceDetail?.ves < 0;
            const isElectric = item.subModels[0]?.specifications
              .fuelType === VEHICLE_VALUE_ELECTRIC_CARS;

            const rebates = [
              isNegativeVes ? 'After VES' : null,
              isNegativeVes && isElectric ? 'EEAI' : null,
            ].filter(Boolean);

            const arfText = rebates.length ? `(${rebates.join(' & ')} rebates)` : '';

            return (
              <div
                key={`${item.subModels[0]?.id}-arf`}
                className={`${styles.containerScrollCell}`}>
                {arfValue}
                {(isNegativeVes || isElectric)
                && (
                  <span className={styles.textSpanContent}>
                    {arfText}
                  </span>
                )}
              </div>
            );
          }
          return (
            <div
              key={`null-${i}-arf`}
              className={`${styles.containerScrollCell}`}>
              -
            </div>
          );
        })}
      </div>
      <div
        className={`${styles.containerStickyRow} ${styles.textLabel}`}>
        VES / CEVS
      </div>
      <div className={`${styles.containerRow}`}>
        {compareList.map((item, i) => {
          if (item !== null) {
            const vesValue = convertPriceToText(
              Math.abs(item.subModels[0]?.priceDetail?.ves ?? 0),
            );
            return (
              <div
                key={`${item.subModels[0]?.id}-ves-cevs`}
                className={`${styles.containerScrollCell}`}>
                {vesValue}
                {item.subModels[0]?.priceDetail.ves
                && (
                  <span className={styles.textSpanContent}>
                    {item.subModels[0]?.priceDetail.ves > 0
                      ? PRICE_INFO_SPAN_CONTENT_SURCHARGE
                      : PRICE_INFO_SPAN_CONTENT_REBATE}
                  </span>
                )}
              </div>
            );
          }
          return (
            <div
              key={`null-${i}-ves-cevs`}
              className={`${styles.containerScrollCell}`}>
              -
            </div>
          );
        })}
      </div>
      <div
        className={`${styles.containerStickyRow} ${styles.textLabel}`}>
        EEAI
      </div>
      <div className={`${styles.containerRow}`}>
        {compareList.map((item, i) => {
          if (item !== null) {
            const eeaiValue = convertPriceToText(
              item.subModels[0]?.priceDetail?.eeai,
            );
            return (
              <div
                key={`${item.subModels[0]?.id}-eeai`}
                className={`${styles.containerScrollCell}`}>
                {eeaiValue}
              </div>
            );
          }
          return (
            <div
              key={`null-${i}-eeai`}
              className={`${styles.containerScrollCell}`}>
              -
            </div>
          );
        })}
      </div>
      <div
        className={`${styles.containerStickyRow} ${styles.textLabel}`}>
        Total Basic Cost
      </div>
      <div className={`${styles.containerRow}`}>
        {compareList.map((item, i) => {
          if (item !== null) {
            const basicCostValue = convertPriceToText(
              item.subModels[0]?.priceDetail?.totalBasicCost,
            );
            return (
              <div
                key={`${item.subModels[0]?.id}-total-basic-cost`}
                className={`${styles.containerScrollCell}`}>
                {basicCostValue}
              </div>
            );
          }
          return (
            <div
              key={`null-${i}-total-basic-cost`}
              className={`${styles.containerScrollCell}`}>
              -
            </div>
          );
        })}
      </div>
      <div
        className={`${styles.containerStickyRow} ${styles.textLabel}`}>
        Premium
      </div>
      <div className={`${styles.containerRow}`}>
        {compareList.map((item, i) => {
          if (item !== null) {
            const premiumValue = item.subModels[0]?.priceDetail?.premium ? `${item.subModels[0]?.priceDetail?.premium}%` : '-';
            return (
              <div
                key={`${item.subModels[0]?.id}-premium`}
                className={`${styles.containerScrollCell}`}>
                {premiumValue}
              </div>
            );
          }
          return (
            <div
              key={`null-${i}-premium`}
              className={`${styles.containerScrollCell}`}>
              -
            </div>
          );
        })}
      </div>
      <div
        className={`${styles.containerStickyRow} ${styles.textLabel}`}>
        Built In
      </div>
      <div className={`${styles.containerRow}`}>
        {compareList.map((item, i) => {
          if (item !== null) {
            return (
              <div
                key={`${item.subModels[0]?.id}-built-in`}
                className={`${styles.containerScrollCell}`}>
                {item.country}
              </div>
            );
          }
          return (
            <div
              key={`null-${i}-price`}
              className={`${styles.containerScrollCell}`}>
              -
            </div>
          );
        })}
      </div>
      <div
        className={`${styles.containerStickyRow} ${styles.textLabel}`}>
        Dealers
      </div>
      <div className={`${styles.containerRow}`}>
        {compareList.map((item, i) => {
          if (item !== null) {
            return (
              <div
                key={`${item.subModels[0]?.id}-dealers`}
                className={`${styles.containerScrollCell}`}>
                {item.parallelDealers && (
                  <TextLink
                    className={styles.link}
                    href={`${joinSgcmMobileSite(`new_cars/overview.php?CarCode=${item.id}&Subcode=${item.subModels[0]?.id}`)}`}
                    text="> Parallel Importers" />
                )}
                {item.authorizeDealers && item.authorizeDealers.map(ad => (
                  <TextLink
                    key={ad.id}
                    className={styles.link}
                    href={`${joinSgcmMobileSite(`new_cars/adpage.php?DID=${ad.id}`)}`}
                    text={`> ${ad.name}`} />
                ))}
              </div>
            );
          }
          return (
            <div
              key={`null-${i}-dealers`}
              className={`${styles.containerScrollCell}`}>
              -
            </div>
          );
        })}
      </div>
      <div
        className={`${styles.containerStickyRow} ${styles.textLabel}`}>
        Editorial Review
      </div>
      <div className={`${styles.containerRow}`}>
        {editorialReviewList.map((editorialReview, i) => {
          if (editorialReview !== undefined) {
            return (
              <div
                key={editorialReview}
                className={cx(
                  styles.containerScrollCell,
                  styles.containerEditorialReviewHeader,
                )}>
                {editorialReview
                  ? (
                    <Image
                      key="tick"
                      alt="tick"
                      height={12}
                      src="https://i.i-sgcm.com/news/tick.svg"
                      width={12} />
                  )
                  : (
                    <Image
                      key="cross"
                      alt="cross"
                      height={12}
                      src="https://i.i-sgcm.com/news/cross.svg"
                      width={12} />
                  )}
                {editorialReview && (
                  <TextLink
                    href={editorialReview}
                    text="Read Review" />
                )}
              </div>
            );
          }
          return (
            <div
              key={`null-${i}-review`}
              className={`${styles.containerScrollCell}`}>
              -
            </div>
          );
        })}
      </div>
    </>
  );
};
